import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import BoundFormGroup from 'shared/components/boundForm/BoundFormGroup';
import useBoundForm from 'shared/hooks/useBoundForm';
import { postMonitor, putMonitor } from 'redux/actions/jobsActions';
import { useSelector } from 'react-redux';
import selectors from 'redux/selectors/selectors';
import { SelectNotificationGroup } from 'features/notifications/select-notification-group';
import { mapBackInterval, marksInterval, marksTimeout, valuesInterval } from './mappings';
import { SelectRegionChecks } from '../regions/select-region-checks';
import { MinRegionCheck } from '../regions/min-region-check';
import { RxCross1 } from 'react-icons/rx';
import { useNavigate } from 'react-router-dom';
import { MonitorModalTitle } from './monitor-modal-title';
import { Backdrop, Body, Footer, Header, Overlay } from '../../shared/components/SlideOut';
import { UpgradeModal } from '../upgrade/upgrade-modal';
import { CustomHttpHeaders } from '../custom-headers/custom-http-headers';
import { CustomHttpAuth } from '../custom-auth/custom-http-auth';
import { HttpTags } from '../tags/http-tags';

const AdvancedLink = styled.div`
  width: 100%;
  text-align: center;
  margin: 12px 0;
`;

const getInitialState = (monitor = {}, isFreeTier = false) => {
  // Handle special case for 30-second checks
  let runIntervalSeconds;
  if (monitor?.run_interval_thirty_seconds) {
    // This is a 30-second check
    runIntervalSeconds = 0; // Index 0 for 30s
  } else {
    // Normal interval calculation
    runIntervalSeconds = mapBackInterval(parseFloat(monitor?.run_interval_seconds || 300) / 60);
  }

  return {
    friendly_name: monitor?.friendly_name || '',
    uri: monitor?.uri || '',
    request_body: monitor?.request_body || '',
    content_type: monitor?.content_type || 'none',
    auth_type: monitor?.auth_type || 'none',
    auth_username: monitor?.auth_username || '',
    auth_password: monitor?.auth_password || '',
    auth_bearer_token: monitor?.auth_bearer_token || '',
    method: monitor?.method || 'GET',
    timeout_ms: parseFloat(monitor?.timeout_ms || 15000) / 1000,
    run_interval_seconds: runIntervalSeconds,
    keyword_search: monitor?.keyword_search || '',
    keyword_search_inverted: monitor?.keyword_search_inverted || false,
    keyword_search_case_sensitive: monitor?.keyword_search_case_sensitive || false,
    keyword_search_regex: monitor?.keyword_search_regex || false,
    monitor_tls: monitor?.monitor_tls ?? true,
    monitor_dns: monitor?.monitor_dns ?? !isFreeTier,
  };
};

const httpMethodOptions = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'HEAD', label: 'HEAD' },
];

const contentTypeOptions = [
  { value: 'none', label: 'None ' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
  { value: 'form_url_encoded', label: 'Form URL Encoded' },
  { value: 'text', label: 'Plain Text' },
];

const SiteSlideout = ({ showSiteSlideout, adding, jobId }) => {
  const navigate = useNavigate();
  const monitor = useSelector(selectors.httpMonitorDetails);
  const isFreeTier = useSelector(selectors.isFreeTier);

  const [showFreeTierUpgrade, setShowFreeTierUpgrade] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [notifications, setNotifications] = useState(monitor?.group_notifications ?? []);
  const [checkRegions, setCheckRegions] = useState(monitor?.check_regions ?? ['aws-us-east-1']);
  const [minRegionCheck, setMinRegionCheck] = useState(monitor?.min_healthy_regions ?? 1);
  const [customHeaders, setCustomHeaders] = useState(monitor?.custom_http_headers ?? {});
  const [tags, setTags] = useState(monitor?.tags ?? []);
  const form = useBoundForm({ initialState: getInitialState(monitor, isFreeTier) });

  const handleIntervalChange = (value) => {
    if (value < 5 && isFreeTier) {
      form.set('run_interval_seconds', 5);
      setShowFreeTierUpgrade(true);
    }
  };

  const handleHttpMethodChange = (value) => {
    if (value !== 'GET' && isFreeTier) {
      form.set('method', 'GET');
      setShowFreeTierUpgrade(true);
    }
  };

  const handleDnsChange = (value) => {
    if (value && isFreeTier) {
      form.set('monitor_dns', false);
      setShowFreeTierUpgrade(true);
    }
  };

  useEffect(() => {
    // unhighlight any highlighted text the screen
    window.getSelection().removeAllRanges();
  }, [showFreeTierUpgrade]);

  const fields = [
    [{ id: 'friendly_name', label: 'Name', placeholder: 'Google', required: true }],
    [
      {
        id: 'uri',
        label: 'URL or IP',
        placeholder: 'https://google.com',
        required: true,
        format: 'urlOrIp',
      },
    ],
    [
      {
        id: 'run_interval_seconds',
        label: 'Check Interval',
        placeholder: 'Select interval',
        type: 'slider',
        marks: marksInterval,
        min: 0,
        max: 12,
        step: 1,
        tooltip: { open: false },
        helpText: 'How often to check your site.',
        onChange: handleIntervalChange,
      },
    ],
  ];

  const fieldsAdvanced = [
    [
      {
        id: 'keyword_search',
        label: 'Keyword',
        placeholder: 'Keyword',
        helpText: `If set, the site will be monitored for the existence of this keyword. If the keyword is not found even if the site is responding, it'll be considered "Down"`,
      },
    ],
    [
      {
        id: 'keyword_search_inverted',
        label: 'Invert',
        type: 'checkbox',
        helpText:
          'If set, the site will be monitored for the absence of this keyword. If the keyword is found even if the site is responding, it\'ll be considered "Down"',
      },
    ],
    [
      {
        id: 'keyword_search_case_sensitive',
        label: 'Case Sensitive',
        type: 'checkbox',
        helpText:
          'If set, the keyword search will be case sensitive. By default, it is case insensitive',
      },
    ],
    [
      {
        id: 'keyword_search_regex',
        label: 'Regex',
        type: 'checkbox',
        helpText:
          'If set, the keyword search will be treated as a Regular Expression (regex). This overrides the "Case Sensitive" and "Invert" options',
      },
    ],
    [
      {
        id: 'timeout_ms',
        label: 'Timeout Seconds',
        type: 'slider',
        marks: marksTimeout,
        min: 1,
        max: 60,
        step: 1,
        helpText: 'How long to wait for a site to respond before considering it "Down"',
      },
    ],
    [
      {
        id: 'method',
        label: 'Method',
        options: httpMethodOptions,
        onChange: handleHttpMethodChange,
      },
    ],
    [
      {
        id: 'request_body',
        label: 'Request Body',
        placeholder: '{ "key": "value" }',
        helpText: 'The body of the request to send to the server',
        type: 'textarea',
      },
    ],
    [
      {
        id: 'content_type',
        label: 'Content Type',
        options: contentTypeOptions,
        helpText: 'The type of the request body',
      },
    ],
  ];

  const fieldsAdvancedCont = [
    [
      {
        id: 'monitor_tls',
        label: 'Monitor TLS/SSL',
        type: 'checkbox',
        helpText: "An option to monitor the domain's TLS/SSL Certificate",
      },
    ],
    [
      {
        id: 'monitor_dns',
        label: 'Monitor DNS',
        type: 'checkbox',
        helpText: "An option to monitor the domain registry's expiry date",
        onChange: handleDnsChange,
      },
    ],
  ];

  const handleHide = () => {
    if (!adding) {
      navigate(`/site/${jobId}`);
      return;
    }
    navigate('/dashboard');
  };

  const toggleAdvanced = () => {
    setShowAdvanced(!showAdvanced);
  };

  const handleSaveClick = () => {
    if (form.state.monitor_tls) {
      if (form.state.uri.startsWith('http://')) {
        form.state.uri = form.state.uri.replace('http://', 'https://');
      }
    }

    if (!/^https?:\/\//.test(form.state.uri) && form.state.uri) {
      form.state.uri = `https://${form.state.uri}`;
    }

    const data = {
      ...form.state,
      run_interval_seconds: valuesInterval[form.state.run_interval_seconds],
      group_notification_ids: notifications.map((notification) => notification.id),
      custom_http_headers: customHeaders,
      check_regions: checkRegions,
      min_healthy_regions: minRegionCheck,
      tags: tags,
    };
    form.validate();
    if (form.isValid) {
      if (jobId) {
        putMonitor(jobId, data).then(() => {
          form.reset(getInitialState());
        });
      } else {
        postMonitor(data).then(() => {
          form.reset(getInitialState());
        });
      }
      handleHide();
    }
  };

  useEffect(() => {
    if (adding) {
      form.reset(getInitialState({}, isFreeTier));
    } else {
      form.reset(getInitialState(monitor));
    }
    setNotifications(monitor?.group_notifications ?? []);
    setCustomHeaders(monitor?.custom_http_headers ?? {});
    setCheckRegions(monitor?.check_regions ?? ['aws-us-east-1']);
    setMinRegionCheck(monitor?.min_healthy_regions ?? 1);
    setTags(monitor?.tags ?? []);
  }, [monitor, adding, isFreeTier]);

  return (
    <>
      <UpgradeModal
        show={showFreeTierUpgrade}
        onHide={() => setShowFreeTierUpgrade(false)}
      />
      <Backdrop
        show={showSiteSlideout}
        onClick={handleHide}
      />
      <Overlay show={showSiteSlideout}>
        <Header>
          <span>{MonitorModalTitle({ adding })}</span>
          <RxCross1
            size={24}
            onClick={handleHide}
            style={{
              cursor: 'pointer',
            }}
          />
        </Header>

        <Body>
          <BoundFormGroup
            formId={form.id}
            fields={fields}
          />
          <SelectNotificationGroup
            value={notifications}
            setValue={setNotifications}
          />

          <AdvancedLink>
            <Button
              size="small"
              onClick={toggleAdvanced}>
              Advanced
            </Button>
          </AdvancedLink>

          {showAdvanced && (
            <>
              <BoundFormGroup
                formId={form.id}
                fields={fieldsAdvanced}
              />
              <CustomHttpHeaders
                values={customHeaders}
                setValues={setCustomHeaders}
              />
              <CustomHttpAuth form={form} />
              <BoundFormGroup
                formId={form.id}
                fields={fieldsAdvancedCont}
              />
              <SelectRegionChecks
                value={checkRegions}
                setValue={setCheckRegions}
                setShowFreeTierUpgrade={setShowFreeTierUpgrade}
              />
              <MinRegionCheck
                value={minRegionCheck}
                setValue={setMinRegionCheck}
                setShowFreeTierUpgrade={setShowFreeTierUpgrade}
              />
              <HttpTags
                values={tags}
                setValues={setTags}
              />
            </>
          )}
        </Body>

        <Footer>
          <Button
            className={'primary'}
            onClick={handleSaveClick}>
            Submit
          </Button>
        </Footer>
      </Overlay>
    </>
  );
};

export default SiteSlideout;
